import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "../Locales/en";
import { translations } from "./Translations";
import {
  CODES,
  DEFAULT_LANG,
  FONT_HREF,
  LANGUAGES,
  getLanguage,
  langFromPath,
  pathFor,
  stripLang,
} from "./i18nPaths";
import "./i18n.css";

export { LANGUAGES, pathFor, stripLang } from "./i18nPaths";

/**
 * اللغة بتتحدد من الرابط: /ar/... عربي، /ru/... روسي، /de/... ألماني، /fr/... فرنساوي، /es/... إسباني،
 * /it/... إيطالي، /he/... عبري، /zh/... صيني،
 * ومن غير بادئة = إنجليزي. كده كل لغة ليها صفحات مستقلة جوجل يقدر يأرشفها.
 */
const isBrowser = typeof window !== "undefined";

export const LANG = isBrowser ? langFromPath(window.location.pathname) : DEFAULT_LANG;

// ⚠️ لازم يتحط في <BrowserRouter basename={BASENAME}> (في App.js)
export const BASENAME = LANG === DEFAULT_LANG ? "/" : `/${LANG}`;

translations.en = en;

// كل لغة في ملف لوحدها، وبيتحمّل بس ملف اللغة المفتوحة (أخف وأسرع)
const LOADERS = {
  ar: () => import("../Locales/ar"),
  ru: () => import("../Locales/ru"),
  de: () => import("../Locales/de"),
  fr: () => import("../Locales/fr"),
  es: () => import("../Locales/es"),
  it: () => import("../Locales/it"),
  he: () => import("../Locales/he"),
  zh: () => import("../Locales/zh"),
};

export const loadLocale = (code = LANG) => {
  if (translations[code]) return Promise.resolve(translations[code]);
  const loader = LOADERS[code];
  if (!loader) return Promise.resolve(en);
  return loader()
    .then((m) => (translations[code] = m.default))
    .catch(() => en);
};

// اتجاه الصفحة واللغة والخط قبل ما React يرسم أي حاجة (من غير وميض)
const applyDocument = (code) => {
  if (!isBrowser) return;
  const l = getLanguage(code);
  const root = document.documentElement;
  root.lang = code;
  root.dir = l.dir;
  const href = FONT_HREF[code];
  if (href && !document.getElementById("lang-font")) {
    const link = document.createElement("link");
    link.id = "lang-font";
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
};

const initial = isBrowser ? loadLocale(LANG) : Promise.resolve(en);
applyDocument(LANG);

const LanguageContext = createContext(null);

// تغيير اللغة = فتح نفس الصفحة بالرابط بتاع اللغة الجديدة
export const goToLang = (code) => {
  if (!CODES.includes(code) || !isBrowser) return;
  try {
    localStorage.setItem("helmax-lang", code);
  } catch {}
  const { pathname, search, hash } = window.location;
  window.location.assign(`${pathFor(code, pathname)}${search}${hash}`);
};

export const LanguageProvider = ({ children }) => {
  const [dict, setDict] = useState(() => translations[LANG] || null);

  useEffect(() => {
    if (!dict) initial.then((d) => setDict(d || en));
    try {
      localStorage.setItem("helmax-lang", LANG);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => {
    const language = getLanguage(LANG);
    return {
      lang: LANG,
      t: dict || en,
      language,
      languages: LANGUAGES,
      locale: language.locale,
      isRTL: language.dir === "rtl",
      // الرابط الحالي بلغة تانية (للروابط في قائمة اللغات)
      hrefFor: (code, path) => pathFor(code, path ?? (isBrowser ? stripLang(window.location.pathname) : "/")),
      setLang: goToLang,
      // للتوافق مع الكود القديم
      toggleLang: () => goToLang(LANG === "ar" ? "en" : "ar"),
    };
  }, [dict]);

  // لحد ما ملف اللغة يتحمّل (جزء من الثانية) مبنرسمش حاجة
  if (!dict) return null;

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
